import { userAction } from '@/util/helpers/ajax-helpers';
import { formatValueToSend } from '@/util/helpers/time-helpers';
import {
  TABLE_SELECTED,
  MODAL_SAVE_CLICKED,
  TABLE_ROWS_LOADED,
  VIEW_LOADED,
  LOADING,
  LAYER_USER_INTERACTION,
  EXCEPTION,
  CHANGE_TAB,
  TABLES_LOADED,
  VIEW_SAVED,
  SEARCH_LOADED,
  SHARED_ROWS_REQUEST_LOADED,
  COLUMNS_FOR_DATA_TABLE,
  REFRESH_RESULTS_CLICKED,
  TEMPLATE_LAYER_ADDED,
  START_NEW_QUERY,
  START_EDITING_LAYER_INLINE,
  INLINE_LAYER_ADDED,
  CHANGE_DATASOURCE,
  SAVED_QUERY_SELECTED,
  RESET_LAYER_IN_EDIT_MODE,
  SAVED_QUERY_UPDATED,
  SHARE_METADATA_LOADED,
  SUGGESTION_APPLIED,
  SUGGESTIONS_CHANGED,
  RESET_DATASOURCE,
  RESET_STEPS_STATE,
} from '../actions/constants';
import {
  FROM,
  WHERE,
  GROUP_BY,
  JOIN,
  ORDER_BY,
  COLUMNS,
  EXPAND,
  SELECT,
  ADD_COLUMN,
  SQL_FROM,
  CSV_FROM,
  REARRANGE_COLUMNS,
  RENAME_COLUMNS,
  AGGREGATE,
} from '../util/enums/EnumLayers';
import { EXPLORER } from '../util/enums/views.constants';
import { DATE, DATETIME } from '../util/ColumnType';
import TemplateLayer from '../components/layers/TemplateLayer';
import LayerFrom from '../components/layers/LayerFrom';
import LayerFromSavedQuery from '../components/layers/LayerFromSavedQuery';
import LayerGroupBy from '../components/layers/LayerGroupBy';
import LayerJoin from '../components/layers/LayerJoin';
import LayerAggregate from '../components/layers/LayerAggregate';
import InlineAggregateLayer from '../components/layers/InlineAggregateLayer';
import InlineFromLayer from '../components/layers/InlineFromLayer';
import LayerOrderBy from '../components/layers/LayerOrderBy';
import LayerSelect from '../components/layers/LayerSelect';
import LayerAddColumn from '../components/layers/LayerAddColumn';
import LayerWhere from '../components/layers/LayerWhere';
import LayerSql from '../components/layers/LayerSql';
import LayerCsv from '../components/layers/LayerCsv';
import OrderByKey from '../components/layers/OrderByKey';
import LayerColumns from '../components/layers/LayerColumns';
import LayerRearrangeColumns from '../components/layers/LayerRearrangeColumns';
import LayerRenameColumns from '../components/layers/LayerRenameColumns';
import LayerExpand from '../components/layers/LayerExpand';
import TableRef from '../util/TableRef';
import LayerBuilder from '../components/layers/LayerBuilder';
import ModalWhere from '../components/modals/ModalWhere';
import ModalOrderBy from '../components/modals/ModalOrderBy';
import ModalGroupBy from '../components/modals/ModalGroupBy';
import ModalAggregate from '../components/modals/ModalAggregate';
import ModalColumnsContainer from '../containers/ModalColumnsContainer';
import ModalJoin from '../components/modals/ModalJoin';
import ModalFrom from '../components/modals/ModalFrom';
import ModalSelect from '../components/modals/ModalSelect';
import ModalAddColumn from '../components/modals/ModalAddColumn';
import ModalSqlEditor from '../components/modals/ModalSqlEditor';
import ModalCsvUpload from '../components/modals/ModalCsvUpload';
import ModalRearrangeColumns from '../components/modals/ModalRearrangeColumns';
import ModalRenameColumns from '../components/modals/ModalRenameColumns';
import Column from '../util/Column';
import ColumnName from '../util/ColumnName';
import ForeignKey from '../util/ForeignKey';
import ViewRef from '../util/ViewRef';
import SearchBox from '../components/SearchBox';
import { ACTION_DISABLE, SUMMARIZE } from '../components/HighlightFeature';
import ModalCustomizeAggregate from '../features/countIf/ModalCustomizeAggregate';
import { getUpdatedLayer } from '../features/countIf/ModalCustomizeAggregate/modalReducer';

const FROM_SAVED_QUERY_ALIAS = 'base';

const TEMPLATE_STEPS_TO_REUSE = [JOIN, WHERE];

const INITIAL_STATE = {
  all: [],
  suggestions: [],
  page: 1,
  loading: false,
  selectedLayerId: null,
  layerInEditMode: null, // the Layer that is currently in edit mode (or null if none)
  lastLayerAdded: null, // the Layer that was just added (so we can show an animation)
  collapsedLayerId: null, // the bottom-most collapsed layer
  selectedView: null, // the ViewRef that is currently being edited (note: if imported from another user, this will be null)
  rowsInvalidated: false,
  layerErrorMsg: null, // string error msg (if selected layer has error msg)
  usedAliases: {}, // maps 'p' -> TableRef
  loadingView: false,
  loadingRowsRequest: false,
  chartingOptions: {}, // customisations applied to charts
  showChartingOptions: false, // true if should show charting instead of results
};

function chooseAlias(tovName, usedAliases) {
  let alias = tovName
    .toLowerCase()
    .replace(/ /, '_')
    .replace(/[^a-z_]+/g, '')
    .split('_')
    .map((x) => x[0])
    .join('');
  if (alias.length === 0) {
    alias = 'x'; // happens if name only contains non-alphanumeric characters (e.g. الاشتراكات لكل كورس)
  }
  if (!(alias in usedAliases)) {
    return alias;
  }
  let i = 1;
  while (alias + i in usedAliases) {
    i++;
  }
  return alias + i;
}

export function buildLayer(layerType, state, aliases, existingLayer) {
  switch (layerType) {
    case WHERE: {
      const clauses = state.clauses.map((clause) => {
        const rhsText =
          clause.expressionType.type() === DATE ||
          clause.expressionType.type() === DATETIME
            ? formatValueToSend(clause.rhsText, clause.lhs.aggregation())
            : clause.rhsText;

        const { lhs } = clause; // ColumnName of left-hand-side
        const { operator } = clause; // operator (e.g. '=')
        const rhsConstant = clause.rhs; // the rhs value if user chose a constant (e.g. NULL, TRUE, etc.)

        let rhsValue = rhsText;
        if (rhsConstant) {
          if (rhsConstant === 'NULL') {
            rhsValue = null;
          } else if (rhsConstant === 'EMPTY') {
            rhsValue = '';
          } else {
            rhsValue = rhsConstant;
          }
        }

        const { expressionType } = clause; // ColumnType
        return new LayerWhere(
          lhs,
          operator,
          rhsValue,
          clause.rhsColumn,
          expressionType,
        );
      });
      const result = clauses[0];
      clauses.slice(1).forEach((clause) => {
        result.clauses().push(clause);
      });
      result.setIsAnd(state.isAnd);
      return result;
    }
    case GROUP_BY: {
      return LayerGroupBy.buildFromState(state);
    }
    case AGGREGATE: {
      return LayerAggregate.buildFromState(state);
    }
    case JOIN: {
      const fk = state.selected;
      const alias = existingLayer
        ? existingLayer.alias() // re-use alias if we can
        : chooseAlias(fk.foreignColumn().tov().name(), aliases.usedAliases);
      aliases.newAliases[alias] = fk.foreignColumn().tov();
      return new LayerJoin(state.joinType, fk, alias, state.includeType);
    }
    case SQL_FROM: {
      const alias = 'sql';
      return new LayerSql(
        state.sql,
        state.columns.map(
          (c) =>
            new Column(
              new ColumnName(alias, c.name().name()),
              c.type(),
              c
                .fks()
                .map(
                  (fk) =>
                    new ForeignKey(
                      new ColumnName(alias, fk.localColumn().name()),
                      fk.foreignColumn(),
                    ),
                ),
            ),
        ),
        alias,
      );
    }
    case FROM: {
      return new LayerFrom(
        state.selected.schema(),
        state.selected.name(),
        existingLayer.alias(),
      );
    }
    case CSV_FROM: {
      const alias = 'csv';
      return new LayerCsv(state.csvUploadId, state.columns, alias);
    }
    case ORDER_BY: {
      const orderByKeys = state.selected.map(
        (o) => new OrderByKey(o.columnName(), o.desc()),
      );
      return new LayerOrderBy(orderByKeys, state.nulls, state.limit);
    }
    case SELECT: {
      return new LayerSelect(state.visible);
    }
    case ADD_COLUMN: {
      return new LayerAddColumn(
        state.name,
        state.expression,
        state.replaceExisting,
      );
    }
    case COLUMNS: {
      return new LayerColumns(state.visible, state.customColumns, false);
    }
    case EXPAND: {
      return new LayerExpand(state.columnName, state.expandType);
    }
    case REARRANGE_COLUMNS: {
      return new LayerRearrangeColumns(state.order);
    }
    case RENAME_COLUMNS: {
      return new LayerRenameColumns(state.existing, state.names);
    }
    default:
      throw new Error(`Unsupported layer:${layerType}`);
  }
}

export function buildUsedAliases(allLayers) {
  const usedAliases = {};
  const firstLayer = allLayers[0];
  if (
    firstLayer instanceof LayerSql ||
    firstLayer instanceof LayerCsv ||
    firstLayer instanceof LayerFromSavedQuery
  ) {
    // first layer is sql or csv
    usedAliases[firstLayer.alias()] = firstLayer.ref();
  } else {
    // first layer is a table
    usedAliases[firstLayer.alias()] = new TableRef(
      firstLayer.schema(),
      firstLayer.table(),
    );
  }
  // identify other used aliases
  allLayers
    .filter((l) => l.type() === JOIN)
    .forEach((l) => {
      usedAliases[l.alias()] = l.fk().foreignColumn().tov();
    });
  return usedAliases;
}

export function buildCollapsedLayerId(layers) {
  // HACK: in old world we remove all template layers, so ignore template layers when deciding where to collapse the layers
  const nonTemplateLayers = layers.filter((l) => !l.isTemplate());
  return nonTemplateLayers.length < 4
    ? null
    : nonTemplateLayers[nonTemplateLayers.length - 3].id();
}

const Layers = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case LOADING:
      if (action.awaiting === TABLE_ROWS_LOADED) {
        return {
          ...state,
          rowsInvalidated: false,
          layerErrorMsg: null, // reset
          loadingView: false, // reset
        };
      }
      if (action.awaiting === VIEW_LOADED) {
        return { ...state, loading: true };
      }
      if (action.awaiting === START_EDITING_LAYER_INLINE) {
        return { ...state, layerInEditMode: action.meta.layer };
      }
      if (action.awaiting === SHARED_ROWS_REQUEST_LOADED) {
        return { ...state, loadingRowsRequest: true };
      }
      return state;
    case RESET_STEPS_STATE:
    case START_NEW_QUERY:
    case CHANGE_DATASOURCE:
      return { ...INITIAL_STATE };
    case SEARCH_LOADED: {
      const layers = action.payload.json._activeLayers.map((l) =>
        LayerBuilder.build(l, action.payload.isNewWorld),
      );
      const firstLayer = layers[0];
      const usedAliases = {};
      usedAliases[firstLayer.alias()] = new TableRef(
        firstLayer.schema(),
        firstLayer.table(),
      );
      return {
        ...INITIAL_STATE,
        all: layers,
        selectedLayerId: layers[layers.length - 1].id(),
        rowsInvalidated: true,
        usedAliases,
      };
    }
    case TEMPLATE_LAYER_ADDED: {
      // new template steps should go before the first summarize step
      const indexOfSelectedLayer = state.all.findIndex(
        (l) => l.id() === state.selectedLayerId,
      );
      const enabledLayers = state.all.slice(0, indexOfSelectedLayer + 1);
      let positionToInsert = enabledLayers.length;
      const { newLayer } = action.payload;

      if (action.payload.layerToAppendTo) {
        // insert directly after chosen layer
        positionToInsert =
          state.all.findIndex(
            (l) => l.id() === action.payload.layerToAppendTo.id(),
          ) + 1;
      } else if (!(newLayer instanceof InlineAggregateLayer)) {
        //
        // insert before first summarize step
        //
        let i = enabledLayers.length - 1;
        // calculate index of first template
        for (; i >= 0; --i) {
          if (enabledLayers[i].isTemplate()) {
            continue;
          }
          break;
        }
        if (newLayer.layerType() === JOIN || newLayer.layerType() === WHERE) {
          // find first non-JOIN step (because JOIN steps should be placed as early as possible and WHERE layers should come directly after JOINs)
          for (; i < enabledLayers.length; ++i) {
            if (
              enabledLayers[i].isTemplate() &&
              enabledLayers[i].layerType() !== JOIN
            ) {
              positionToInsert = i;
              break;
            }
          }
        } else {
          // find first summarize step (because summarize steps should be placed as late possible)
          for (; i < enabledLayers.length; ++i) {
            if (
              enabledLayers[i].isTemplate() &&
              enabledLayers[i] instanceof InlineAggregateLayer
            ) {
              positionToInsert = i;
              break;
            }
          }
        }
      }

      const layersAbove = enabledLayers.slice(0, positionToInsert);
      const layersBelow = enabledLayers.slice(positionToInsert);
      return {
        ...state,
        all: [
          ...layersAbove,
          newLayer,
          ...layersBelow,
          ...state.all.slice(indexOfSelectedLayer + 1), // disabled layers
        ],
        selectedLayerId:
          layersBelow.length === 0
            ? newLayer.id() // select new layer
            : state.selectedLayerId, // keep existing step selected
        lastLayerAdded: newLayer,
      };
    }
    case CHANGE_TAB: {
      if (action.payload.tab === EXPLORER && state.showChartingOptions) {
        return { ...state, showChartingOptions: false };
      }
      return state;
    }
    case TABLE_SELECTED: {
      SearchBox.clearSearchBox(); // search-box is no longer consistent with loaded query
      const alias = chooseAlias(action.payload.table.name(), []);
      const usedAliases = {};
      usedAliases[alias] = action.payload.table;
      const schema = action.payload.table.schema();
      const table = action.payload.table.name();
      const all = [
        new InlineFromLayer(schema, table, alias),
        action.payload.doingOnboarding ? null : new TemplateLayer(JOIN),
        new TemplateLayer(WHERE),
        new InlineAggregateLayer(),
      ];
      return {
        ...INITIAL_STATE,
        all: all.filter(Boolean), // remove nulls
        selectedLayerId: all[all.length - 1].id(),
        rowsInvalidated: true,
        usedAliases,
      };
    }
    case SAVED_QUERY_SELECTED: {
      const usedAliases = {};
      usedAliases[FROM_SAVED_QUERY_ALIAS] = ViewRef.fromSavedQuery(
        action.payload,
      );
      const all = [
        LayerFromSavedQuery.createFromActionPayload(
          FROM_SAVED_QUERY_ALIAS,
          action.payload,
        ),
        new TemplateLayer(JOIN),
        new TemplateLayer(WHERE),
        new InlineAggregateLayer(),
      ];
      return {
        ...INITIAL_STATE,
        all,
        selectedLayerId: all[all.length - 1].id(),
        rowsInvalidated: true,
        usedAliases,
      };
    }
    case SAVED_QUERY_UPDATED: {
      const {
        usedAliases: oldUsedAliases,
        all: [_oldStartFromSavedQueryStep, ...steps],
      } = state;

      const usedAliases = {
        ...oldUsedAliases,
        [FROM_SAVED_QUERY_ALIAS]: ViewRef.fromSavedQuery(action.payload),
      };

      const all = [
        LayerFromSavedQuery.createFromActionPayload(
          FROM_SAVED_QUERY_ALIAS,
          action.payload,
        ),
        ...steps,
      ];

      return {
        ...state,
        all,
        selectedLayerId: all[all.length - 1].id(),
        rowsInvalidated: true,
        usedAliases,
      };
    }
    // NOTE: this case is a hack. Find/architect a proper way after refactoring.
    case TABLE_ROWS_LOADED: {
      if (state.all[0] instanceof LayerFromSavedQuery) {
        const joinStepsAliasesMap = state.all.reduce((acc, step) => {
          if (step.type() === JOIN) {
            acc[step.alias()] = state.usedAliases[step.alias()];
          }
          return acc;
        }, {});
        const savedQueryAliasValue = state.usedAliases[state.all[0].alias()];
        const usedAliasesWithReplacedValue = Object.fromEntries(
          Object.entries(action.payload._dataTable._aliases).map(([alias]) => [
            alias,
            joinStepsAliasesMap[alias] ?? savedQueryAliasValue,
          ]),
        );

        return {
          ...state,
          usedAliases: {
            ...state.usedAliases,
            ...usedAliasesWithReplacedValue,
          },
        };
      }

      return state;
    }
    case REFRESH_RESULTS_CLICKED:
    case TABLES_LOADED: {
      return {
        ...state,
        rowsInvalidated: state.all.length > 0, // table schema has been reloaded, so need to check that layers are still consistent (or retry if query has already failed)
      };
    }
    case VIEW_SAVED:
      if (action.payload.renaming) {
        if (
          !state.selectedView ||
          state.selectedView.series() !== action.payload.savedView.series()
        ) {
          return state;
        }
      }
      return { ...state, selectedView: action.payload.savedView };
    case SHARED_ROWS_REQUEST_LOADED: {
      const { activeSteps, inactiveSteps } = action.payload.json.reduce(
        (grouped, step) => {
          if (step._active) {
            grouped.activeSteps.push(LayerBuilder.build(step));
          } else {
            grouped.inactiveSteps.push(LayerBuilder.build(step));
          }
          return grouped;
        },
        {
          activeSteps: [],
          inactiveSteps: [],
        },
      );
      const layers = [...activeSteps, ...inactiveSteps];
      const usedAliases = buildUsedAliases(layers);
      const collapsedLayerId = buildCollapsedLayerId(activeSteps);
      return {
        ...INITIAL_STATE,
        all: layers,
        selectedLayerId: activeSteps[activeSteps.length - 1]?.id(),
        rowsInvalidated: true,
        loadingRowsRequest: false, // reset
        usedAliases,
        collapsedLayerId,
        chartingOptions: state.chartingOptions,
        showChartingOptions: state.showChartingOptions,
      };
    }
    case VIEW_LOADED: {
      SearchBox.clearSearchBox(); // search-box is no longer consistent with loaded query
      const activeLayers = action.payload.json._activeLayers.map((l) =>
        LayerBuilder.build(l, action.payload.newWorld),
      );
      const inactiveLayers = action.payload.json._inactiveLayers.map((l) =>
        LayerBuilder.build(l, action.payload.newWorld),
      );
      const allLayers = [...activeLayers, ...inactiveLayers];
      const usedAliases = buildUsedAliases(allLayers);
      const collapsedLayerId = buildCollapsedLayerId(activeLayers);
      return {
        ...INITIAL_STATE,
        all: allLayers,
        chartingOptions: action.payload.json._chartConfig,
        showChartingOptions: action.payload.showChartingOptions,
        selectedLayerId: activeLayers[activeLayers.length - 1].id(),
        rowsInvalidated: true,
        loadingView: true,
        usedAliases,
        loading: false,
        collapsedLayerId,
        selectedView:
          /* action.payload.imported ? null : */ action.payload.view,
      };
    }

    case RESET_DATASOURCE:
      return INITIAL_STATE;

    case EXCEPTION: {
      if (
        [TABLE_ROWS_LOADED, COLUMNS_FOR_DATA_TABLE, VIEW_SAVED].includes(
          action.awaiting,
        ) &&
        action.payload.layerId
      ) {
        const invalidLayer = state.all.find(
          (l) => l.id() === action.payload.layerId,
        );
        if (!invalidLayer) {
          // happens if results in ShareHtml fail to load (because we don't have layers)
          return state;
        }
        return {
          ...state,
          selectedLayerId: invalidLayer.id(),
          layerErrorMsg: action.userMsg,
        };
      }
      if (action.awaiting === SHARED_ROWS_REQUEST_LOADED) {
        return {
          ...state,
          loadingRowsRequest: false, // reset
        };
      }
      return state;
    }
    case LAYER_USER_INTERACTION: {
      switch (action.payload.event) {
        case 'selected': {
          if (state.selectedLayerId === action.payload.props.layerId) {
            // user just wants to refresh
            return { ...state, rowsInvalidated: true };
          }
          // identify the layers that are being moved
          const start = state.all.findIndex(
            (l) => l.id() === state.selectedLayerId,
          );
          const end = state.all.findIndex(
            (l) => l.id() === action.payload.props.layerId,
          );
          let moved = state.all.slice(start + 1, end + 1);
          if (moved.length === 0) {
            moved = state.all.slice(end + 1, start + 1);
          }

          const selectedStepIndex = state.all.findIndex(
            (step) => step._id === action.payload.props.layerId,
          );

          const stepsToActivate = state.all.slice(0, selectedStepIndex + 1);
          const stepsToKeepInactive = state.all.slice(selectedStepIndex + 1);

          return {
            ...state,
            all: [
              ...stepsToActivate.map((s) => s.cloneAndupdateActive(true)),
              ...stepsToKeepInactive.map((s) => s.cloneAndupdateActive(false)),
            ],
            selectedLayerId: action.payload.props.layerId,
            page: 1,
            rowsInvalidated:
              moved.filter((l) => l.enabled() && !l.isTemplate()).length > 0, // only if at least one enabled (and non-template) layer was moved
          };
        }
        case 'toggle-charting': {
          return { ...state, showChartingOptions: action.payload.newValue };
        }
        case 'update-charting-options': {
          return { ...state, chartingOptions: action.payload.chartingOptions };
        }
        case 'toggled': {
          // update 'enabled' flag
          userAction(ACTION_DISABLE);
          const indexOfToggledLayer = state.all.findIndex(
            (l) => l.id() === action.payload.props.layerId,
          );
          const toggledLayer = state.all[indexOfToggledLayer];
          const updatedCopyOfToggledLayer = toggledLayer.cloneAndupdateEnabled(
            !toggledLayer.enabled(),
          );
          const indexOfSelectedLayer = state.all.findIndex(
            (l) => l.id() === state.selectedLayerId,
          );
          return {
            ...state,
            all: [
              ...state.all.slice(0, indexOfToggledLayer),
              updatedCopyOfToggledLayer,
              ...state.all.slice(indexOfToggledLayer + 1),
            ],
            page: 1,
            rowsInvalidated: indexOfToggledLayer <= indexOfSelectedLayer, // only if the toggled layer is one of the active layers
          };
        }
        case 'deleted': {
          const indexOfDeletedLayer = state.all.findIndex(
            (l) => l.id() === action.payload.props.layerId,
          );
          let indexOfSelectedLayer = state.all.findIndex(
            (l) => l.id() === state.selectedLayerId,
          );
          const rowsInvalidated =
            indexOfDeletedLayer <= indexOfSelectedLayer &&
            state.all[indexOfDeletedLayer].enabled() &&
            !state.all[indexOfDeletedLayer].isTemplate();
          if (indexOfSelectedLayer === indexOfDeletedLayer) {
            indexOfSelectedLayer--;
          }
          const selectedLayerId = state.all[indexOfSelectedLayer].id();
          return {
            ...state,
            all: [
              ...state.all.slice(0, indexOfDeletedLayer),
              ...state.all.slice(indexOfDeletedLayer + 1),
            ],
            page: 1,
            selectedLayerId,
            rowsInvalidated,
          };
        }
        case 'updated': {
          return updateLayer(
            action.payload,
            state,
            ({ props }) => props.updatedLayer,
          );
        }
        case 'nextPage': {
          return { ...state, page: state.page + 1, rowsInvalidated: true };
        }
        case 'prevPage': {
          return { ...state, page: state.page - 1, rowsInvalidated: true };
        }
        case 'collapse': {
          return { ...state, collapsedLayerId: action.payload.props.layerId };
        }
        case 'expand': {
          return { ...state, collapsedLayerId: null };
        }
        case 'remove': {
          const indexOfSelectedLayer = state.all.findIndex(
            (l) => l.id() === state.selectedLayerId,
          );
          return {
            ...state,
            all: [...state.all.slice(0, indexOfSelectedLayer + 1)],
            page: 1,
          };
        }
        default:
          throw new Error(`unexpected event type: ${action.payload.event}`);
      }
    }
    case SHARE_METADATA_LOADED: {
      return {
        ...state,
        chartingOptions: action.payload.metadata?.chartingOptions || {},
      };
    }

    case SUGGESTION_APPLIED: {
      userAction('use-suggested-step');
      const newLayer = action.payload.newLayer;
      const layerToReplace = action.payload.layerToReplace;
      const layer = layerToReplace
        ? newLayer.cloneAndUpdateId(layerToReplace.id())
        : newLayer;
      const layerId = layerToReplace
        ? layerToReplace.id()
        : state.selectedLayerId;
      const indexOfSelectedLayer = state.all.findIndex(
        (l) => l.id() === layerId,
      );
      const splitIndex = layerToReplace
        ? indexOfSelectedLayer
        : indexOfSelectedLayer + 1;
      const aboveSteps = state.all.slice(0, splitIndex);
      const belowSteps = state.all.slice(
        layerToReplace ? splitIndex + 1 : splitIndex,
      );
      return {
        ...state,
        all: [...aboveSteps, layer, ...belowSteps],
        rowsInvalidated: true,
        selectedLayerId: layerToReplace ? state.selectedLayerId : layer.id(),
      };
    }

    case SUGGESTIONS_CHANGED: {
      return {
        ...state,
        suggestions: action.payload,
      };
    }

    case INLINE_LAYER_ADDED:
      userAction(`use-inline-${action.payload.modal.modalType()}`);
    // fallthrough!
    case MODAL_SAVE_CLICKED: {
      switch (action.payload.modal) {
        case EXPAND:
        case ModalWhere:
        case ModalOrderBy:
        case ModalGroupBy:
        case ModalAggregate:
        case ModalColumnsContainer:
        case ModalAddColumn:
        case ModalSelect:
        case ModalRearrangeColumns:
        case ModalRenameColumns:
        case ModalJoin: {
          // create new layer or update existing
          const indexOfSelectedLayer = state.all.findIndex(
            (l) => l.id() === state.selectedLayerId,
          );
          const aliases = { usedAliases: state.usedAliases, newAliases: {} };
          const newLayer =
            action.payload.modal === EXPAND
              ? buildLayer(
                  action.payload.modal,
                  action.payload.state,
                  null,
                  null,
                )
              : buildLayer(
                  action.payload.modal.modalType(),
                  action.payload.state,
                  aliases,
                  action.payload.existingLayer,
                );
          const layerToReplace =
            action.payload.existingLayer || action.payload.templateToReplace;
          if (layerToReplace) {
            // replacing existing layer
            const willReplaceExisting =
              !layerToReplace.isTemplate() ||
              !TEMPLATE_STEPS_TO_REUSE.includes(layerToReplace.layerType());
            let { selectedLayerId } = state;
            if (layerToReplace.id() === state.selectedLayerId) {
              if (willReplaceExisting) {
                selectedLayerId = newLayer.id(); // new layer is the bottom selected step
              }
            }
            // NOTE: disabling this, as we want to re-enable disabled steps if they are edited
            // if(!layerToReplace.enabled()) {
            //     newLayer = newLayer.cloneAndupdateEnabled(false);//layer is disabled
            // }
            const indexOfExisting = state.all.findIndex(
              (l) => l.id() === layerToReplace.id(),
            );
            const layerAfterNewLayer = willReplaceExisting
              ? indexOfExisting + 1 // replace existing step
              : indexOfExisting; // keep template step
            return {
              ...state,
              all: [
                ...state.all.slice(0, indexOfExisting),
                newLayer,
                ...state.all.slice(layerAfterNewLayer),
              ],
              page: 1,
              selectedLayerId,
              rowsInvalidated:
                indexOfExisting <= indexOfSelectedLayer && newLayer.enabled(),
              usedAliases: {
                ...aliases.usedAliases,
                ...aliases.newAliases,
              },
            };
          }
          // creating new layer
          let layersAbove = state.all.slice(0, indexOfSelectedLayer + 1);
          if (
            (action.payload.modal === ModalSelect &&
              layersAbove[indexOfSelectedLayer].type() === SELECT) ||
            (action.payload.modal === ModalOrderBy &&
              layersAbove[indexOfSelectedLayer].type() === ORDER_BY) ||
            (action.payload.modal === ModalRearrangeColumns &&
              layersAbove[indexOfSelectedLayer].type() === REARRANGE_COLUMNS)
          ) {
            // two SELECT or REARRANGE or ORDER_BY layers next to each other is not needed - so slice off the old one
            layersAbove = layersAbove.slice(0, indexOfSelectedLayer);
          }
          // place new layer in sensible position
          let layersBelow = [];
          // place new layer before any template steps
          for (let i = layersAbove.length - 1; i >= 0; --i) {
            if (layersAbove[i].isTemplate()) {
              continue;
            }
            layersBelow = layersAbove.slice(i + 1);
            layersAbove = layersAbove.slice(0, i + 1);
            break;
          }
          // if one of the layersBelow is a template step of the right type, place next to it
          const indexOfTemplateToReplace = layersBelow.findIndex(
            (template) => template.layerType() === newLayer.type(),
          );
          const replace = !TEMPLATE_STEPS_TO_REUSE.includes(newLayer.type()); // if not JOIN or WHERE, replace template step, instead of placing next to
          if (indexOfTemplateToReplace > -1) {
            layersAbove = [
              ...layersAbove,
              ...layersBelow.slice(0, indexOfTemplateToReplace),
            ];
            layersBelow = [
              ...layersBelow.slice(
                indexOfTemplateToReplace + (replace ? 1 : 0),
              ),
            ];
          }

          // Replace last querybuilder step if the new one is type SORT and the last active one is active and type SORT too
          const lastAboveItem = layersAbove[layersAbove.length - 1];
          if (
            newLayer.type() === ORDER_BY &&
            lastAboveItem.type() === ORDER_BY &&
            !lastAboveItem.isTemplate() &&
            lastAboveItem.enabled()
          ) {
            layersAbove.pop();
          }

          return {
            ...state,
            all: [
              ...layersAbove,
              newLayer,
              ...layersBelow,
              ...state.all.slice(indexOfSelectedLayer + 1),
            ],
            page: 1,
            selectedLayerId:
              layersBelow.length === 0 ? newLayer.id() : state.selectedLayerId,
            lastLayerAdded: newLayer,
            rowsInvalidated: true,
            usedAliases: {
              ...aliases.usedAliases,
              ...aliases.newAliases,
            },
          };
        }
        case ModalCsvUpload:
        case ModalFrom:
        case ModalSqlEditor: {
          const fromLayer = buildLayer(
            action.payload.modal.modalType(),
            action.payload.state,
            null,
            action.payload.existingLayer,
          );
          const fresh = [
            fromLayer,
            new TemplateLayer(JOIN),
            new TemplateLayer(WHERE),
            new InlineAggregateLayer(),
          ];
          const all = action.payload.existingLayer
            ? [fromLayer, ...state.all.slice(1)] // replace first layer
            : fresh; // start fresh
          const selectedLayerId = !action.payload.existingLayer
            ? fresh[fresh.length - 1].id() // new query, so use last step as selected
            : action.payload.existingLayer.id() === state.selectedLayerId
            ? fromLayer.id() // replacing selected step, so maintain selected
            : state.selectedLayerId; // else, don't change selected step
          const usedAliases = {};
          usedAliases[fromLayer.alias()] = fromLayer.ref();
          return {
            ...INITIAL_STATE,
            all,
            selectedView: action.payload.existingLayer
              ? state.selectedView
              : null, // if editing existing layer, keep existing view selected
            selectedLayerId,
            rowsInvalidated: true,
            usedAliases,
          };
        }
        case ModalCustomizeAggregate:
          return updateLayer(action.payload, state);
        default:
          return state;
      }
    }
    case RESET_LAYER_IN_EDIT_MODE:
      return { ...state, layerInEditMode: null };
    default:
      return state;
  }
};

export function updateLayer(
  payload,
  state,
  getUpdatedLayerFn = getUpdatedLayer,
) {
  const updatedLayer = getUpdatedLayerFn(payload);
  const indexOfUpdatedLayer = state.all.findIndex(
    (l) => l.id() === updatedLayer.id(),
  );
  const actionName = `use-inline-${updatedLayer.type()}`;
  const meta =
    actionName === SUMMARIZE
      ? {
          numberOfMetrics: updatedLayer.functions().length,
          numberOfDimensions: updatedLayer.keys().length,
        }
      : undefined;

  userAction(actionName, meta);
  const updated = {
    ...state,
    all: [
      ...state.all.slice(0, indexOfUpdatedLayer),
      updatedLayer,
      ...state.all.slice(indexOfUpdatedLayer + 1),
    ],
    page: 1,
    rowsInvalidated: true,
    layerInEditMode: updatedLayer,
  };
  // NOTE: this case is a hack. Find/architect a proper way after refactoring.
  return state.all[0] instanceof LayerFromSavedQuery
    ? updated
    : { ...updated, usedAliases: buildUsedAliases(updated.all) };
}

export default Layers;
